import { useEffect, useState } from "react";
import AppShell from "@/components/layout/AppShell";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { ShieldCheck, Search } from "lucide-react";
import { toast } from "sonner";

export default function AuditLogs() {
    const [logs, setLogs] = useState([]);
    const [summary, setSummary] = useState({ total: 0, by_action: {} });
    const [q, setQ] = useState("");

    const load = async () => {
        try {
            const [l, s] = await Promise.all([
                q ? api.get(`/audit/logs?user_email=${encodeURIComponent(q)}`) : api.get("/audit/logs"),
                api.get("/audit/summary"),
            ]);
            setLogs(l.data); setSummary(s.data);
        } catch (e) { toast.error(formatApiError(e)); }
    };
    useEffect(() => { load(); }, []);

    return (
        <AppShell title="Audit Logs" subtitle={`${summary.total} total events`}>
            <div className="grid grid-cols-4 gap-3 mb-4">
                <Card className="p-4 border border-slate-200 rounded-md shadow-none">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Total Events</div>
                    <div className="text-2xl font-display font-bold mt-1">{summary.total}</div>
                </Card>
                {Object.entries(summary.by_action || {}).slice(0, 3).map(([k, v]) => (
                    <Card key={k} className="p-4 border border-slate-200 rounded-md shadow-none">
                        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{k}</div>
                        <div className="text-2xl font-display font-bold mt-1">{v}</div>
                    </Card>
                ))}
            </div>
            <div className="flex gap-2 mb-3">
                <div className="relative flex-1 max-w-sm">
                    <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <Input placeholder="Filter by user email..." value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => e.key === "Enter" && load()} className="pl-9" data-testid="audit-search" />
                </div>
            </div>
            <Card className="border border-slate-200 rounded-md overflow-hidden shadow-none">
                <table className="w-full">
                    <thead className="bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-500 uppercase tracking-wider text-left">
                        <tr><th className="px-4 py-3">Timestamp</th><th className="px-4 py-3">User</th><th className="px-4 py-3">Role</th><th className="px-4 py-3">Action</th><th className="px-4 py-3">Entity</th></tr>
                    </thead>
                    <tbody>
                        {logs.map(l => (
                            <tr key={l.id} className="border-b border-slate-100 hover:bg-slate-50">
                                <td className="px-4 py-3 text-xs text-slate-500">{l.timestamp?.slice(0, 19).replace("T", " ")}</td>
                                <td className="px-4 py-3 text-xs font-mono">{l.user_email}</td>
                                <td className="px-4 py-3 text-xs uppercase tracking-wider">{l.user_role?.replace(/_/g, " ")}</td>
                                <td className="px-4 py-3">
                                    <span className="status-badge bg-slate-100 text-slate-700 border-slate-200">{l.action}</span>
                                </td>
                                <td className="px-4 py-3 text-xs">{l.entity} <span className="text-slate-400 font-mono">{l.entity_id?.slice(-8)}</span></td>
                            </tr>
                        ))}
                        {logs.length === 0 && <tr><td colSpan="5" className="text-center py-10 text-sm text-slate-500">No audit logs yet. Actions will appear as users interact with the system.</td></tr>}
                    </tbody>
                </table>
            </Card>
        </AppShell>
    );
}
